import { Box, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { ReactNode, useState } from 'react';

interface TabPanelProps {
 children: ReactNode;
 index: number;
 value: number;
 keepMounted?: boolean;
}
interface TabItemProps {
 id: string;
 label: string;
 content: ReactNode;
 disabled?: boolean;
}
interface ColModalTabsResponsiveProps {
 tabs: TabItemProps[];
 defaultTab?: number;
 variant?: 'fullWidth' | 'standard' | 'scrollable';
 ariaLabel?: string;
 sx?: SxProps<Theme>;
 tabSx?: SxProps<Theme>;
 panelSx?: SxProps<Theme>;
 wrapSx?: SxProps<Theme>;
 onChange?: (newValue: number) => void;
 keepMounted?: boolean;
}

function a11yProps(index: number) {
 return {
  id: `ColModalTabsResponsive-tab-${index}`,
  'aria-controls': `ColModalTabsResponsive-tabpanel-${index}`,
 };
}

function CustomTabPanel(props: TabPanelProps) {
 const { children, value, index, keepMounted = false } = props;
 const isSelected = value === index;

 return (
  <Box
   role="tabpanel"
   id={`ColModalTabsResponsive-tabpanel-${index}`}
   aria-labelledby={`ColModalTabsResponsive-tab-${index}`}
   aria-hidden={!isSelected}
   sx={{
    flex: isSelected ? 1 : 0,
    minHeight: 0,
    flexDirection: 'column',
    overflow: 'hidden',
    display: isSelected ? 'flex' : 'none',
   }}
  >
   {(keepMounted || isSelected) && (
    <Box
     sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
     }}
    >
     {children}
    </Box>
   )}
  </Box>
 );
}

const ColModalTabsResponsive = ({
 tabs,
 defaultTab = 0,
 variant = 'fullWidth',
 ariaLabel = 'Tabs',
 sx,
 tabSx,
 panelSx,
 wrapSx,
 onChange,
 keepMounted = false,
}: ColModalTabsResponsiveProps) => {
 const [tab, setTab] = useState(defaultTab);

 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  setTab(newValue);
  onChange?.(newValue);
 };

 const defaultTabSx = {
  '& .Mui-selected': {
   backgroundColor: (theme: Theme) => theme.palette.kemek.white,
  },
  '& .MuiButtonBase-root.MuiTab-root': {
   pt: '12px',
   pb: '12px',
   fontSize: '14px',
  },
 };

 const defaultPanelSx = {
  p: 0,
 };

 return (
  <Box
   sx={{
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...wrapSx,
   }}
  >
   <Box
    sx={{
     borderBottom: 1,
     borderBottomColor: (theme: Theme) => theme.palette.kemek.lightestGray,
     mt: '-1px',
     flexShrink: 0,
     ...sx,
    }}
   >
    <Tabs
     variant={variant}
     value={tab}
     onChange={handleChange}
     aria-label={ariaLabel}
     sx={{
      ...defaultTabSx,
      ...tabSx,
     }}
    >
     {tabs.map((tabItem, index) => (
      <Tab
       key={tabItem.id}
       label={tabItem.label}
       disabled={tabItem.disabled}
       {...a11yProps(index)}
      />
     ))}
    </Tabs>
   </Box>

   {tabs.map((tabItem, index) => (
    <CustomTabPanel
     key={tabItem.id}
     value={tab}
     index={index}
     keepMounted={keepMounted}
    >
     <Box
      sx={{
       py: 2,
       px: 3,
       flex: 1,
       minHeight: 0,
       display: 'flex',
       flexDirection: 'column',
       overflow: 'hidden',
       ...defaultPanelSx,
       ...panelSx,
      }}
     >
      {tabItem.content}
     </Box>
    </CustomTabPanel>
   ))}
  </Box>
 );
};

export default ColModalTabsResponsive;
